"use client";

import type { MenuProps } from "antd";
import { Button, Dropdown, Layout, Menu, Drawer } from "antd";
import Image from "next/image";
import React, { useEffect, useMemo, useState } from "react";
import { twMerge } from "tailwind-merge";
import styles from "./styles.module.scss";
import { ECookieKeys } from "@/const/cookie-keys.const";
import Cookies from "js-cookie";
import SvgIcon from "../svg-icon";
import { usePathname, useRouter } from "next/navigation";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useUserStore } from "@/store/use-user-store";
import { InitUser } from "../init-user";
import { useUserInfo } from "@/hooks/use-user-info";
import Notification from "./notifcation";
import Link from "next/link";

const { Header, Content, Sider } = Layout;

const WebLayout: React.FC = ({ children }: { children: React.ReactNode }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const router = useRouter();
  const token = Cookies.get(ECookieKeys.TOKEN);
  const { userInfo, setUserInfo } = useUserStore();
  const { refetch } = useUserInfo();
  const pathname = usePathname();
  const [selectedKey, setSelectedKey] = useState("");
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const menuItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "/sequences",
        icon: <SvgIcon path="/account_tree.svg" className={styles.icon} />,
        label: "Sequence",
        children: [
          { key: "/sequence", label: "シーケンス" },
          { key: "/templates/email", label: "メールテンプレート一覧" },
          { key: "/templates/message", label: "チャットテンプレート一覧" },
          { key: "/series", label: "一斉配信" },
        ],
      },
      {
        key: "/chat",
        icon: <SvgIcon path="/admin/chat.svg" className={styles.icon} />,
        label: "Inbox",
      },
      {
        key: "customer",
        icon: <SvgIcon path="/admin/users.svg" className={styles.icon} />,
        label: "CRM(顧客管理)",
        children: [
          { key: "/customer/list", label: "顧客一覧" },
          { key: "/list", label: "リスト" },
          { key: "/customer/sell", label: "営業" },
        ],
      },

      {
        key: "todo",
        icon: <SvgIcon path="/icons/dns.svg" className={styles.icon} />,
        label: "Todo",
        children: [
          { key: "/calendar", label: "カレンダー" },
          { key: "/task", label: "タスク" },
        ],
      },
      {
        key: "setting",
        icon: <SvgIcon path="/admin/settings.svg" className={styles.icon} />,
        label: "Setting",
        children: [
          (userInfo?.is_ceo || userInfo?.role?.display_name === "管理者") && {
            key: "/setting/accounts",
            label: "アカウント設定",
          },
          { key: "/setting/properties", label: "プロパティ設定" },
          (userInfo?.is_ceo ||
            (userInfo?.permissions && userInfo?.permissions?.value !== 0)) && {
            key: "/setting/link-sns",
            label: "データ連携設定",
          },
          { key: "/setting/plan", label: "契約プラン確認・変更 " },
          { key: "/setting/business-days", label: "営業日設定" },
        ],
      },
    ],
    [userInfo]
  );

  // Hàm tìm parent key
  const findParentKey = (items: any[], targetKey: string): string | null => {
    for (const item of items) {
      if (!item) continue;
      if (item.children) {
        const found = item.children.some(
          (child: any) => child && child.key === targetKey
        );
        if (found) return item.key;

        // Tìm đệ quy trong children
        const parentKey = findParentKey(item.children, targetKey);
        if (parentKey) return parentKey;
      }
    }
    return null;
  };

  // Hàm tìm key từ URL (giữ nguyên)
  const findKeyByUrl = (items: any[], url: string): string | null => {
    for (const item of items) {
      if (!item) continue;
      if (item?.key === url) {
        return item.key;
      }
      if (item?.children) {
        const childKey = findKeyByUrl(item.children, url);
        if (childKey) return childKey;
      }
    }
    return null;
  };

  // useEffect để set selected key và open keys
  useEffect(() => {
    const key = findKeyByUrl(menuItems, pathname);
    if (pathname === "/profile") {
      setSelectedKey("/setting/link-sns"); // ← Active link-sns thay vì setting
      setOpenKeys(["setting"]); // ← Mở menu setting
      return; // ← Quan trọng: return luôn để không chạy logic bên dưới
    }
    if (key) {
      setSelectedKey(key);

      // Tìm và mở parent menu
      const parentKey = findParentKey(menuItems, key);
      if (parentKey) {
        setOpenKeys([parentKey]);
      }
    }
  }, [pathname, menuItems]);

  const handleOpenChange = (keys: string[]) => {
    setOpenKeys(keys);
  };

  // Check if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    if (pathname?.startsWith("/auth")) return;

    const t = Cookies.get(ECookieKeys.TOKEN); // đọc trực tiếp trong effect
    if (!t) {
      localStorage.clear();
      router.replace("/auth/login");
      return;
    }
    refetch().then((res) => {
      if (res.data) {
        if (!res.data?.data?.user?.company?.id) {
          router.replace(
            `/auth/register-company?userId=${res.data?.data?.user?.id}`
          );
        } else if (!res.data?.data?.user?.plan_detail?.plan_id) {
          router.replace(`/contract?userId=${res.data?.data?.user?.id}`);
        }
        setUserInfo({
          ...res.data?.data?.user,
          purchased_packages: res?.data?.data?.purchased_packages,
        });
      }
    });
  }, [token]);

  const signOut = () => {
    Cookies.remove(ECookieKeys.TOKEN);
    useUserStore.getState().clearUserInfo();
    localStorage.clear();
    router.replace("/auth/login");
  };

  const goToProfile = () => {
    router.replace("/profile");
  };

  const dropdownItems: MenuProps["items"] = [
    {
      key: "1",
      label: (
        <Button
          type="link"
          className="!text-[#666666] !font-bold"
          onClick={goToProfile}
        >
          個人アカウント設定
        </Button>
      ),
    },
    {
      key: "2",
      label: (
        <Button
          type="link"
          className="!text-[#666666] !font-bold"
          onClick={signOut}
        >
          ログアウト
        </Button>
      ),
    },
  ];

  const handleMenuClick = (e: any) => {
    router.replace(e?.key);
    // Close mobile drawer after navigation
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const renderSidebarContent = () => (
    <div className={twMerge("h-full flex flex-col", styles.sidebar)}>
      {!isMobile && (
        <div
          className={twMerge(
            "flex w-full p-2",
            collapsed ? "justify-center" : "justify-end"
          )}
        >
          <Button
            type="text"
            className="!bg-white !w-[25px] !h-[25px] !rounded-[2px]"
            icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />
        </div>
      )}
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]} // ← Thay đổi ở đây
        openKeys={openKeys} // ← Thêm openKeys
        onOpenChange={handleOpenChange} // ← Xử lý khi mở/đóng submenu
        items={menuItems}
        className="!bg-[#333333] flex-1 !border-r-0"
        onClick={handleMenuClick}
      />
    </div>
  );

  return (
    <Layout className={twMerge(styles.webLayout, "min-h-screen")}>
      <Header className="flex items-center justify-between px-4 !h-16">
        <div className="flex items-center gap-3">
          {isMobile && (
            <Button
              type="text"
              icon={
                <div className="flex flex-col gap-1">
                  <div className="w-5 h-0.5 bg-white"></div>
                  <div className="w-5 h-0.5 bg-white"></div>
                  <div className="w-5 h-0.5 bg-white"></div>
                </div>
              }
              onClick={toggleMobileDrawer}
              className="!text-white hover:!bg-gray-700"
            />
          )}
          <Image
            src="/logo-admin.svg"
            alt="Vercel logomark"
            width={150}
            height={60}
          />
        </div>
        <div className="flex gap-x-3 px-4 items-center">
          <div className="text-white">{userInfo?.company?.name}</div>
          <Dropdown menu={{ items: dropdownItems }} placement="bottomLeft">
            <Image
              src="/admin/account_circle.svg"
              alt=""
              width={20}
              height={20}
              className="cursor-pointer"
            />
          </Dropdown>
          <Link href="/setting/accounts">
            <Image src="/admin/settings.svg" alt="" width={20} height={20} />
          </Link>
          <Notification />
        </div>
      </Header>

      <Layout style={{ minHeight: "calc(100vh - 64px)" }}>
        {/* Desktop Sidebar */}
        {!isMobile && (
          <Sider
            width={200}
            style={{ background: "#333333" }}
            collapsed={collapsed}
            className="!bg-[#333333]"
          >
            {renderSidebarContent()}
          </Sider>
        )}

        {/* Mobile Drawer */}
        {isMobile && (
          <Drawer
            title={null}
            placement="left"
            onClose={() => setMobileDrawerOpen(false)}
            open={mobileDrawerOpen}
            width={250}
            className="!p-0"
            bodyStyle={{ padding: 0, background: "#333333" }}
            headerStyle={{ display: "none" }}
          >
            {renderSidebarContent()}
          </Drawer>
        )}

        <Layout>
          <Content className={twMerge("py-8 !pb-24", isMobile && "!py-4")}>
            <InitUser />
            {children}
          </Content>
        </Layout>
      </Layout>
    </Layout>
  );
};

export default WebLayout;
